import { Controller } from "@hotwired/stimulus"

// New-UI comment editor: a minimal Froala instance (toolbar matched to the concept:
// bold / italic / list / link / attach / image). Attach to the form; it finds the
// textarea within and replaces it. Clears on a successful Turbo submit.
//
// Property-change updates morph-refresh the show page; Turbo's idiomorph can't reconcile
// Froala's injected DOM with the server <textarea>, which wipes the editor. So we (1) tell
// the morph to leave the editor's container alone, and (2) as a safety net, rebuild the
// editor if a morph wiped it anyway.
export default class extends Controller {
  connect() {
    this.skipMorph = this.skipMorph.bind(this)
    this.onMorph = this.onMorph.bind(this)
    document.addEventListener("turbo:before-morph-element", this.skipMorph)
    document.addEventListener("turbo:morph", this.onMorph)
    this.initEditor()
  }

  disconnect() {
    document.removeEventListener("turbo:before-morph-element", this.skipMorph)
    document.removeEventListener("turbo:morph", this.onMorph)
    this.destroyEditor()
  }

  initEditor() {
    const ta = this.element.querySelector("textarea")
    // Re-init whenever there's a textarea but no live Froala instance. Guarding on the actual
    // .fr-box (not a dataset flag) is what makes "Reply & resolve" work: closing the ticket
    // fires a broadcasts_refreshes morph that re-attaches this data-turbo-permanent composer,
    // triggering disconnect→connect. destroyEditor() tore the editor down, but the old flag-based
    // guard then wrongly skipped re-init, leaving a bare <textarea>. Checking .fr-box self-corrects
    // (and still prevents double-init — a live editor already has a .fr-box).
    if (!ta || this.element.querySelector(".fr-box")) return

    const buttons = ["bold", "italic", "formatUL", "insertLink", "insertFile", "insertImage"]
    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    this.editor = new window.FroalaEditor(ta, {
      key: "fIE3A-9C2F2F2B6B4C1E4ud1BI1IMNBUMRWAi1AYMSTRBUZYB-16D4E3D2A2C3I2F1B10A2E2==",
      attribution: false,
      charCounterCount: false,
      quickInsertEnabled: false,
      toolbarSticky: false,
      pastePlain: false,
      heightMin: 110,
      placeholderText: "Write a reply…",
      imageUploadURL: "/ticketupload",
      fileUploadURL: "/ticketupload",
      imageMaxSize: 1024 * 1024 * 3,
      requestHeaders: { "X-CSRF-Token": csrf },
      toolbarButtons: buttons,
      toolbarButtonsMD: buttons,
      toolbarButtonsSM: buttons,
      toolbarButtonsXS: ["bold", "italic", "insertLink", "insertImage"],
    })
  }

  destroyEditor() {
    if (this.editor) { try { this.editor.destroy() } catch (_) {} this.editor = null }
  }

  // Tell Turbo's morph to leave the editor's container (and its Froala DOM) untouched.
  skipMorph(event) {
    const body = this.element.querySelector(".addcomment-body")
    if (body && event.target === body) event.preventDefault()
  }

  // Safety net: if a morph wiped the editor in place (no disconnect/reconnect), rebuild it.
  onMorph() {
    if (!this.element.querySelector(".fr-box")) {
      this.destroyEditor()
      this.initEditor()
    }
  }

  clear(event) {
    if (event.detail?.success && this.editor) this.editor.html.set("")
  }
}
